import React, { useState, useEffect, useRef } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, Button, IconButton, Input, Option, Select, Avatar
} from "@material-tailwind/react";
import { ChevronDownIcon, PlusCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import getData from "../../../api/axiosInstance";
import {toLocalDateTimeInputValue} from "../../../utils/dateTime";

export default function AddRefundDialog({ isOpen, onClose, onSuccess, requestHeaders }) {

    const [newRefundData, setNewRefundData] = useState({
        refundDate: toLocalDateTimeInputValue(),
        paidPrice: '',
        refundedAmount: 0,
        status: 'ISSUED',
        purchasedItem: ''
    });

    const [allItemList, setAllItemList] = useState([]);
    const [itemSearchTerm, setItemSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Fetch all items for dropdown
    useEffect(() => {
        if (isOpen) {
            getData(
                "/purchased_items/all_legacy",
                requestHeaders,
                setAllItemList,
                "Error fetching item data"
            );
        }
    }, [isOpen, requestHeaders]);

    // Set refundDate on open
    useEffect(() => {
        if (isOpen) {
            setNewRefundData(prev => ({ ...prev, refundDate: toLocalDateTimeInputValue(new Date(), true) }));
        }
    }, [isOpen]);

    // Auto-fill paidPrice when item is selected
    useEffect(() => {
        if (newRefundData.purchasedItem) {
            const selectedItem = allItemList.find(manga => manga.id === newRefundData.purchasedItem);
            if (selectedItem && selectedItem.price !== undefined && selectedItem.price !== null) {
                setNewRefundData(prevState => ({
                    ...prevState,
                    paidPrice: selectedItem.price
                }));
            }
        }
    }, [newRefundData.purchasedItem, allItemList]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isDropdownOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isDropdownOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                triggerRef.current && !triggerRef.current.contains(event.target)
            ) {
                setIsDropdownOpen(false);
                setItemSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const openDropdown = (el) => {
        if (el) {
            const rect = el.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
        }
        setItemSearchTerm('');
        setIsDropdownOpen(true);
    };

    const filteredItems = allItemList
        .filter(manga => {
            const searchWords = itemSearchTerm.toLowerCase().split(' ').filter(Boolean);
            return searchWords.every(word => manga.title.toLowerCase().includes(word));
        })
        .sort((a, b) => new Date(b.purchasedDate) - new Date(a.purchasedDate))
        .slice(0, 20);

    const selectedItem = allItemList.find(m => m.id === newRefundData.purchasedItem);

    const handleSelect = (manga) => {
        setNewRefundData(prev => ({ ...prev, purchasedItem: manga.id }));
        setIsDropdownOpen(false);
        setItemSearchTerm('');
    };

    const handleClearSelection = (e) => {
        e.stopPropagation();
        setNewRefundData(prev => ({ ...prev, purchasedItem: '', paidPrice: '' }));
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setNewRefundData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setNewRefundData({
            refundDate: toLocalDateTimeInputValue(),
            paidPrice: '',
            refundedAmount: 0,
            status: 'ISSUED',
            purchasedItem: ''
        });
        setItemSearchTerm('');
        setIsDropdownOpen(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            const purchasedItem = allItemList.find(manga => manga.id === newRefundData.purchasedItem);
            if (!purchasedItem) return;

            const dataToSend = { ...newRefundData, purchasedItem };

            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

            await axios.post(
                `${API_URL}/refunded_items/add`,
                dataToSend,
                { headers: requestHeaders }
            );

            resetForm();
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Error adding refund:", err);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog
            size="l"
            open={isOpen}
            handler={handleClose}
            className="p-4 mt-10 mb-10"
        >
            <DialogHeader className="relative m-0 block">
                <Typography variant="h4" color="blue-gray">
                    Add refund
                </Typography>
                <Typography className="mt-1 font-normal text-gray-600">
                    Import refunded item.
                </Typography>
                <IconButton
                    size="sm"
                    variant="text"
                    className="!absolute right-3.5 top-3.5"
                    onClick={handleClose}
                >
                    <XMarkIcon className="h-4 w-4 stroke-2" />
                </IconButton>
            </DialogHeader>

            <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4 overflow-visible mb-4">

                    {/* Choose Item */}
                    <div ref={triggerRef}>
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                            Choose Item
                        </Typography>

                        {!isDropdownOpen ? (
                            // Zatvoreno — originalni button stil, prikazuje selektovano ili placeholder
                            <button
                                type="button"
                                onClick={(e) => openDropdown(e.currentTarget)}
                                className="w-full bg-white border border-blue-gray-200 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-4 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
                            >
                                {selectedItem ? (
                                    <div className="flex items-center">
                                        <Avatar
                                            className="w-10 h-10 rounded-full"
                                            src={selectedItem.imageUrl}
                                            alt="Item"
                                        />
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-900">{selectedItem.title}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-gray-500">Select Item</span>
                                )}
                                <div className="flex items-center gap-1">
                                    {selectedItem && (
                                        <span onClick={handleClearSelection} className="text-gray-400 hover:text-gray-600">
                                            <XMarkIcon className="h-4 w-4" />
                                        </span>
                                    )}
                                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                </div>
                            </button>
                        ) : (
                            // Otvoreno — originalni search input stil, direktno na mestu triggera
                            <input
                                ref={(el) => { searchInputRef.current = el; }}
                                type="text"
                                placeholder="Search Item"
                                value={itemSearchTerm}
                                onChange={(e) => setItemSearchTerm(e.target.value)}
                                onFocus={(e) => {
                                    const rect = e.currentTarget.closest('[ref]')?.getBoundingClientRect()
                                        || e.currentTarget.getBoundingClientRect();
                                    setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
                                }}
                                className="w-full px-3 py-2 border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Paid Price (£)
                            </Typography>
                            <Input
                                type="number"
                                step="0.01"
                                color="gray"
                                size="lg"
                                placeholder="eg. 100.00"
                                name="paidPrice"
                                value={newRefundData.paidPrice}
                                onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900"
                                containerProps={{ className: "!min-w-full" }}
                                labelProps={{ className: "hidden" }}
                            />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Refunded Amount (RSD)
                            </Typography>
                            <Input
                                type="number"
                                step="0.01"
                                color="gray"
                                size="lg"
                                placeholder="eg. 100.00"
                                name="refundedAmount"
                                value={newRefundData.refundedAmount}
                                onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900"
                                containerProps={{ className: "!min-w-full" }}
                                labelProps={{ className: "hidden" }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Status
                            </Typography>
                            <Select
                                name="status"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'status', value } })}
                                value={newRefundData.status}
                            >
                                <Option value="ISSUED">Issued</Option>
                                <Option value="COMPLETED">Completed</Option>
                                <Option value="CANCELED">Canceled</Option>
                            </Select>
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Refund date
                            </Typography>
                            <Input
                                type="datetime-local"
                                color="gray"
                                size="lg"
                                placeholder="Select date and time"
                                name="refundDate"
                                value={newRefundData.refundDate}
                                onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900"
                                containerProps={{ className: "!min-w-full" }}
                                labelProps={{ className: "hidden" }}
                            />
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="flex-shrink-0">
                    <Button type="submit" className="mv-primary-button ml-auto">
                        <PlusCircleIcon className="h-4 w-4" />
                        Add Refund
                    </Button>
                </DialogFooter>
            </form>

            {/* Fixed dropdown lista */}
            {isDropdownOpen && (
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'fixed',
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        zIndex: 9999,
                    }}
                    className="bg-white border border-blue-gray-200 rounded-md shadow-lg"
                >
                    <ul className="max-h-60 overflow-y-auto">
                        {filteredItems.length === 0 ? (
                            <div className="p-3 text-center text-gray-500">No items found</div>
                        ) : (
                            filteredItems.map((manga) => {
                                const formattedDate = new Date(manga.purchasedDate).toLocaleString("en-US", {
                                    year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
                                });
                                return (
                                    <li
                                        key={manga.id}
                                        onClick={() => handleSelect(manga)}
                                        className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                                    >
                                        <div className="flex items-center">
                                            <Avatar className="w-10 h-10 rounded-full" src={manga.imageUrl} alt={manga.title} />
                                            <div className="ml-4">
                                                <p className="text-sm font-medium text-gray-900">{manga.title}</p>
                                                <p className="text-sm text-gray-500">
                                                    {formattedDate}
                                                    <span className="mx-2 text-gray-800">&bull;</span>
                                                    <span className="text-purple-800 dark:text-purple-400">£{manga.price}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </Dialog>
    );
}
